import { isTypingTarget } from "@/lib/isTypingTarget";

export type RailShortcutSection = "home" | "actions" | "codesmith" | "proof";

const RAIL_SHORTCUTS = new Map<string, RailShortcutSection>([
  ["h", "home"],
  ["a", "actions"],
  ["c", "codesmith"],
  ["p", "proof"],
]);

/** Shown in the rail tooltips, keyed by the row each letter opens. */
export const RAIL_SHORTCUT_LABELS: Record<RailShortcutSection, string> = {
  home: "H",
  actions: "A",
  codesmith: "C",
  proof: "P",
};

/**
 * How long bare letters stay inert after a keystroke into a text field. Focus
 * can slip mid-sentence (a field disabled on submit, Escape blurring it, a
 * menu closing), and the next letter is still meant as text.
 */
const TYPING_FLOW_GRACE_MS = 1500;

/**
 * The rail row a keydown's bare-letter shortcut opens, or null when the key
 * is text, a chord, or aimed at a dialog. `lastTextEntryAt` is the
 * `timeStamp` of the last keydown into a text field.
 */
export function railShortcutSection(
  e: KeyboardEvent,
  lastTextEntryAt: number,
): RailShortcutSection | null {
  if (typeof e.key !== "string") return null;
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return null;
  if (e.repeat || e.isComposing || e.defaultPrevented) return null;
  const section = RAIL_SHORTCUTS.get(e.key.toLowerCase());
  if (!section) return null;
  if (isTypingTarget(e.target)) return null;
  if (
    e.target instanceof Element &&
    e.target.closest('[role="dialog"], [role="alertdialog"]')
  ) {
    return null;
  }
  if (e.timeStamp - lastTextEntryAt < TYPING_FLOW_GRACE_MS) return null;
  return section;
}
